import { useQuery } from '@tanstack/react-query'

/** Severity levels returned by the Atlassian Statuspage API, in ascending order. */
export type StatusIndicatorValue = 'none' | 'minor' | 'major' | 'critical'

type SystemStatus = {
  indicator: StatusIndicatorValue
  description: string
  pageUrl: string
}

/**
 * Subset of the Atlassian Statuspage public API v2 `/status.json` response shape.
 * Only fields consumed by this application are included; all others are omitted.
 * `indicator` uses `unknown` because it is validated at runtime via `isValidIndicator`.
 *
 * Full API response shape and available endpoints: https://status.omnea.co/api/v2
 * Atlassian Statuspage API reference: https://developer.statuspage.io
 */
type AtlassianStatusResponse = {
  status?: { indicator?: unknown; description?: unknown }
  page?: { url?: unknown }
}

const STATUS_PAGE_URL = 'https://status.omnea.co'
const STATUS_API_URL = `${STATUS_PAGE_URL}/api/v2/status.json`

const VALID_INDICATORS = new Set<string>(['none', 'minor', 'major', 'critical'] satisfies StatusIndicatorValue[])

const isValidIndicator = (value: unknown): value is StatusIndicatorValue =>
  typeof value === 'string' && VALID_INDICATORS.has(value)

const fetchSystemStatus = async (): Promise<SystemStatus> => {
  const response = await fetch(STATUS_API_URL)
  if (!response.ok) throw new Error(`Status API returned ${response.status}`)

  const data = (await response.json()) as AtlassianStatusResponse

  if (!isValidIndicator(data.status?.indicator)) {
    throw new Error('Invalid status indicator value')
  }

  return {
    indicator: data.status.indicator,
    description: typeof data.status.description === 'string' ? data.status.description : 'Status Unknown',
    pageUrl: typeof data.page?.url === 'string' ? data.page.url : STATUS_PAGE_URL,
  }
}

export const useSystemStatus = () => {
  return useQuery<SystemStatus, Error>({
    queryKey: ['system-status'],
    queryFn: fetchSystemStatus,
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10_000),
  })
}
